import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';

export const MOBILE_VIEWER_QUERY = '(max-width: 720px), (max-height: 500px)';

const SWIPE_DISTANCE = 48;

interface PhotographStageHandlers {
  onAdvance: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onToggleChrome: () => void;
}

const isMobileViewer = () =>
  typeof window !== 'undefined' && window.matchMedia(MOBILE_VIEWER_QUERY).matches;

export function useMobileViewer(): boolean {
  const [mobile, setMobile] = useState(isMobileViewer);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_VIEWER_QUERY);
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return mobile;
}

/**
 * A tap or click on the photograph advances to the next one.
 * On a phone, a horizontal swipe still moves either direction.
 * Chrome is no longer toggled by tapping the photograph.
 */
export function usePhotographStage(handlers: PhotographStageHandlers) {
  const start = useRef<{ x: number; y: number; id: number; type: string } | null>(null);
  const consumed = useRef(false);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    start.current = {
      x: event.clientX,
      y: event.clientY,
      id: event.pointerId,
      type: event.pointerType,
    };
    consumed.current = false;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // A real touch already owns the pointer. Capture is only a convenience.
    }
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    const origin = start.current;
    start.current = null;
    if (!origin || origin.id !== event.pointerId) return;

    const dx = event.clientX - origin.x;
    const dy = event.clientY - origin.y;
    const swiped = isMobileViewer() && Math.abs(dx) >= SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy);

    if (swiped) {
      consumed.current = true;
      if (dx < 0) handlersRef.current.onNext();
      else handlersRef.current.onPrevious();
      return;
    }

    if (isMobileViewer() && origin.type !== 'mouse') {
      consumed.current = true;
      if (Math.hypot(dx, dy) < 12) handlersRef.current.onAdvance();
    }
  };

  const onPointerCancel = () => {
    start.current = null;
    consumed.current = true;
  };

  const onClick = (event: ReactMouseEvent<HTMLElement>) => {
    if (consumed.current) {
      consumed.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    handlersRef.current.onAdvance();
  };

  return { onPointerDown, onPointerUp, onPointerCancel, onClick };
}
